import { useMemo, useState } from "react";


const clamp = (x, min, max) => Math.min(max, Math.max(min, x));
const lerp = (a, b, t) => a + (b - a) * t;

function scoreLikelihood(probPerYear) {
  // Mapping: 0%..30% -> 1..5 (didaktisch, nicht "real")
  const s = Math.ceil((probPerYear / 0.30) * 5);
  return clamp(s, 1, 5);
}

function scoreImpact(eur) {
  // Mapping: 0..2,5 Mio -> 1..5
  const s = Math.ceil((eur / 2_500_000) * 5);
  return clamp(s, 1, 5);
}

function Heatmap({ likelihood, impact, size = 280 }) {
  const cells = 5;
  const cell = size / cells;

  // y=1 oben (niedrig) bis y=5 unten (hoch) ist unüblich;
  // wir nehmen: oben niedrig, unten hoch -> Punkt wird nach unten "riskanter".
  // likelihood: 1 (selten) .. 5 (häufig)
  // impact:     1 (klein)  .. 5 (groß)
  const cx = (impact - 0.5) * cell;
  const cy = (likelihood - 0.5) * cell;

  const labelStyle = { fontSize: 12, opacity: 0.85 };

  const rects = [];
  for (let y = 1; y <= cells; y++) {
    for (let x = 1; x <= cells; x++) {
      // einfache "Schwere": x + y (2..10)
      const severity = x + y;
      // Farbe via HSL: grün (niedrig) -> rot (hoch)
      const hue = lerp(120, 0, (severity - 2) / 8);
      const fill = `hsl(${hue} 70% 88%)`;

      rects.push(
        <rect
          key={`${x}-${y}`}
          x={(x - 1) * cell}
          y={(y - 1) * cell}
          width={cell}
          height={cell}
          fill={fill}
          stroke="rgba(0,0,0,0.12)"
        />
      );
    }
  }

  return (
    <div className="crfHeatmapWrap">
      <svg
        className="crfHeatmap"
        width={size}
        height={size + 40}
        viewBox={`0 0 ${size} ${size + 40}`}
        role="img"
        aria-label="Risikomatrix: Wahrscheinlichkeit (vertikal) und Auswirkung (horizontal)"
      >
        {/* Grid */}
        <g>{rects}</g>

        {/* Punkt */}
        <circle
          cx={cx}
          cy={cy}
          r={10}
          fill="rgba(0,0,0,0.12)"
        />
        <circle
          cx={cx}
          cy={cy}
          r={7}
          fill="white"
          stroke="rgba(0,0,0,0.6)"
          strokeWidth="2"
        />

        {/* Achsenbeschriftung */}
        <text x={0} y={size + 22} style={labelStyle}>
          Auswirkung →
        </text>
        <text
          x={0}
          y={14}
          style={labelStyle}
        >
          Wahrscheinlichkeit ↓
        </text>

        {/* kleine Skala unten */}
        {[1, 2, 3, 4, 5].map((n) => (
          <text
            key={`xlab-${n}`}
            x={(n - 0.5) * cell}
            y={size + 18}
            textAnchor="middle"
            style={{ fontSize: 11, opacity: 0.7 }}
          >
            {n}
          </text>
        ))}
        {/* kleine Skala links */}
        {[1, 2, 3, 4, 5].map((n) => (
          <text
            key={`ylab-${n}`}
            x={6}
            y={(n - 0.5) * cell + 4}
            style={{ fontSize: 11, opacity: 0.7 }}
          >
            {n}
          </text>
        ))}
      </svg>
      <div className="crfHeatmapHint">
        Punkt wandert: je besser das Management, desto “weiter nach links/oben”.
      </div>
    </div>
  );
}

export default function CyberRiskFigure({
  title = "Cyberrisikomanagement – interaktiver Effekt (Beispiel)",
  caption = "Didaktisches Modell mit Beispielwerten (keine echte Risikoanalyse).",
  initial = 0, // 0..100
  // Beispielwerte: "Ohne" (Start) vs. "Mit" (Ziel)
  without = {
    probMajorIncidentPerYear: 0.25,  // 25% / Jahr
    impactEur: 2_000_000,            // €
    detectDays: 30,                  // Tage
    recoverDays: 14,                 // Tage
  },
  withMgmt = {
    probMajorIncidentPerYear: 0.08,  // 8% / Jahr
    impactEur: 900_000,              // €
    detectDays: 3,
    recoverDays: 2,
  },
}) {
  const [maturity, setMaturity] = useState(() => clamp(initial, 0, 100));
  const t = maturity / 100;

  const eur = useMemo(
    () => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }),
    []
  );

  const metrics = useMemo(() => {
    const prob = lerp(without.probMajorIncidentPerYear, withMgmt.probMajorIncidentPerYear, t);
    const impact = lerp(without.impactEur, withMgmt.impactEur, t);
    const detect = lerp(without.detectDays, withMgmt.detectDays, t);
    const recover = lerp(without.recoverDays, withMgmt.recoverDays, t);

    const expectedAnnualLoss = prob * impact; // sehr vereinfachte Erwartungswert-Idee

    return { prob, impact, detect, recover, expectedAnnualLoss };
  }, [t, without, withMgmt]);

  const baselineLoss = without.probMajorIncidentPerYear * without.impactEur;
  const ratio = clamp(metrics.expectedAnnualLoss / baselineLoss, 0, 1);
  const reductionPct = Math.round((1 - ratio) * 100);

  const likelihood = scoreLikelihood(metrics.prob);
  const impactScore = scoreImpact(metrics.impact);

  const label =
    maturity < 25 ? "Nahe „ohne“: vieles passiert ungeplant."
    : maturity < 60 ? "Grundlegend geregelt: erste klare Verbesserungen."
    : maturity < 85 ? "Gut aufgestellt: Risiken sinken spürbar."
    : "Sehr gut: schnelle Erkennung, geringere Auswirkung.";

  return (
    <figure className="crfRoot">
      <div className="crfHeader">
        <div>
          <h3 className="crfTitle">{title}</h3>
          <div className="crfSub">{label}</div>
        </div>

        <div className="crfSlider">
          <label className="crfSliderLabel" htmlFor="crmaturity">
            Umsetzungsgrad: <strong>{maturity}</strong>/100
          </label>
          <input
            id="crmaturity"
            className="crfRange"
            type="range"
            min="0"
            max="100"
            value={maturity}
            onChange={(e) => setMaturity(Number(e.target.value))}
            aria-label="Reifegrad Cyberrisikomanagement"
          />
          <div className="crfSliderEnds">
            <span>Ohne</span>
            <span>Mit</span>
          </div>
        </div>
      </div>

      <div className="crfGrid">
        <div className="crfPanel">
          <div className="crfPanelTitle">Kennzahlen (Beispiel)</div>

          <div className="crfCards">
            <div className="crfCard">
              <div className="crfCardTitle">Schwerer Vorfall</div>
              <div className="crfCardValue">{Math.round(metrics.prob * 100)}% / Jahr</div>
              <div className="crfCardHint">Wahrscheinlichkeit</div>
            </div>

            <div className="crfCard">
              <div className="crfCardTitle">Auswirkung</div>
              <div className="crfCardValue">{eur.format(Math.round(metrics.impact))}</div>
              <div className="crfCardHint">Schaden pro Ereignis</div>
            </div>

            <div className="crfCard">
              <div className="crfCardTitle">Entdeckung</div>
              <div className="crfCardValue">{Math.round(metrics.detect)} Tage</div>
              <div className="crfCardHint">bis bemerkt</div>
            </div>

            <div className="crfCard">
              <div className="crfCardTitle">Wiederherstellung</div>
              <div className="crfCardValue">{Math.round(metrics.recover)} Tage</div>
              <div className="crfCardHint">bis stabil</div>
            </div>
          </div>

          <div className="crfBarBlock" aria-label="Erwarteter Jahresschaden">
            <div className="crfBarTop">
              <div className="crfBarTitle">Erwarteter Jahresschaden (sehr grob)</div>
              <div className="crfBarValue">{eur.format(Math.round(metrics.expectedAnnualLoss))}</div>
            </div>

            <div className="crfBarTrack" role="img" aria-label="Balken: relativ zum Ohne-Zustand">
              <div className="crfBarFill" style={{ width: `${ratio * 100}%` }} />
            </div>

            <div className="crfBarHint">
              Gegenüber „ohne“: <strong>{reductionPct}%</strong> weniger (im Modell).
            </div>
          </div>
        </div>

        <div className="crfPanel">
          <div className="crfPanelTitle">Risikomatrix (5×5)</div>
          <Heatmap likelihood={likelihood} impact={impactScore} />
          <div className="crfMiniExplain">
            <div><strong>Wahrscheinlichkeit:</strong> {likelihood}/5</div>
            <div><strong>Auswirkung:</strong> {impactScore}/5</div>
          </div>
        </div>
      </div>

      <figcaption className="crfCaption">{caption}</figcaption>
    </figure>
  );
}